import type { ComponentType, ReactElement } from 'react';
import { memo } from 'react';

import { BpkSpacing, BpkVStack } from '@skyscanner-internal/backpack-web/bpk-component-layout';
import * as fromCombinedResults from '@web-platform/shared-banana-redux/src/state/combinedResults';
import { HOMEPAGE_FALLBACK_CARD_REASONS } from '@web-platform/shared-universal-constants/src/adverts';
import ErrorBoundary from '@web-platform/shared-universal-ui-error-boundary';
import { logWarn } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import { useSelector } from 'react-redux';

import { GraphicPromoFallbackCard } from '../GraphicPromoFallbackCard/GraphicPromoFallbackCard';

import { EmptyCard } from './EmptyCard';
import { GraphicPromoCard } from './GraphicPromoCard';

import type { GraphicPromoById } from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/entitiesSearchResults/adverts/graphicPromo';

export const GraphicPromoCardController: ComponentType<{}> = memo((): ReactElement => {
  const graphicPromos = useSelector(fromCombinedResults.graphicPromos);
  const status = useSelector(fromCombinedResults.getCreativesStatus);
  switch (status) {
    case 'starting':
    case 'loading':
      return <EmptyCard />;

    case 'error':
      return (
        <GraphicPromoFallbackCard
          fallbackReason={HOMEPAGE_FALLBACK_CARD_REASONS.AD_RESPONSE_FAILURE}
        />
      );

    case 'timeout':
      return (
        <GraphicPromoFallbackCard
          fallbackReason={HOMEPAGE_FALLBACK_CARD_REASONS.AD_RESPONSE_TIMEOUT}
        />
      );

    case 'ready':
      if (Object.keys(graphicPromos).length === 0) {
        return (
          <GraphicPromoFallbackCard
            fallbackReason={HOMEPAGE_FALLBACK_CARD_REASONS.AD_RESPONSE_SERVER_NO_ADVERT_AVAILABLE}
          />
        );
      }

      // NOTE: The data always goes through the reducer, which should format it or fail. As such, the error boundary
      // here will most likely not be needed, but I added it to fail gracefully if there in case of data corruption.
      return (
        <ErrorBoundary
          componentName="GraphicPromoCard"
          fallback={
            <GraphicPromoFallbackCard
              fallbackReason={HOMEPAGE_FALLBACK_CARD_REASONS.AD_CONTENT_BROKEN}
            />
          }
          onError={({ component, error }) =>
            logWarn({
              component,
              error,
              message: 'A data error occurred while rendering the Graphic Promo.',
              data: JSON.stringify(graphicPromos),
            })
          }
        >
          <GraphicPromosDisplay {...graphicPromos} />
        </ErrorBoundary>
      );

    default:
      return <EmptyCard />;
  }
});

const GraphicPromosDisplay = (graphicPromos: GraphicPromoById) => (
  <BpkVStack gap={BpkSpacing.Base} align="stretch">
    {Object.values(graphicPromos)
      .sort((left, right) =>
        left.analyticsProperties.idPlacement.localeCompare(right.analyticsProperties.idPlacement),
      )
      .map((graphicPromo) => (
        <GraphicPromoCard key={graphicPromo.analyticsProperties.idCreative} advert={graphicPromo} />
      ))}
  </BpkVStack>
);
